type I18nMap = {
  [lang: string]: string;
};

type MatchingPath = {
  sql: string;
};

type NodeData = {
  id: string;
  type: string;
  naturalKey: string;
  i18n: I18nMap;
  matchingPathes?: MatchingPath[];
  children?: NodeData[];
};

type ReferenceScopeData = {
  id: string;
  datatype: string;
  i18n: I18nMap;
  nodes: NodeData[];
};

export class ReferenceScope {
  class: string;
  id: string;
  datatype: string;
  i18n: I18nMap;
  nodes: Node[];
  requiredAuthorizations: string[];

  constructor(data: ReferenceScopeData, requiredAuthorizations: string[]) {
    this.class = "ReferenceScope";
    this.id = data.id;
    this.datatype = data.datatype;
    this.i18n = data.i18n;
    this.requiredAuthorizations = requiredAuthorizations;
    this.nodes = (data.nodes || []).map(
      node => new Node(node, null, requiredAuthorizations),
    );
  }

  findNodeById(id: string): Node | null {
    const findNode = (nodes: Node[]): Node | null => {
      for (const node of nodes) {
        if (node.id === id) return node;
        if (node.children) {
          const found = findNode(node.children);
          if (found) return found;
        }
      }
      return null;
    };

    return findNode(this.nodes);
  }

  isNodeChecked(nodeId: string): boolean {
    const node = this.findNodeById(nodeId);
    return node ? node.checked : false;
  }

  isDescendantOf(descendantPath: string, ancestorPath: string): boolean {
    const descendantNode = this.findNodeById(descendantPath);
    const ancestorNode = this.findNodeById(ancestorPath);

    if (!descendantNode || !ancestorNode) return false;

    let currentNode: Node | ReferenceScope | null = descendantNode;
    while (currentNode) {
      if (currentNode === ancestorNode) {
        return true;
      }
      if (!(currentNode instanceof ReferenceScope)) {
        currentNode = currentNode?.parent;
      }
    }
    return false;
  }
}

class Node {
  class: string;
  id: string;
  type: string;
  naturalKey: string;
  i18n: I18nMap;
  requiredAuthorizations: string[];
  children: Node[];
  matchingPathes: MatchingPath[];
  parent: Node | ReferenceScope | null;
  checked: boolean;
  indeterminate: boolean;

  constructor(data: NodeData, parent: Node | ReferenceScope | null, requiredAuthorizations: string[]) {
    this.class = "Node";
    this.id = data.id;
    this.type = data.type;
    this.naturalKey = data.naturalKey;
    this.i18n = data.i18n;
    this.requiredAuthorizations = requiredAuthorizations;
    this.parent = parent;

    this.matchingPathes = data.matchingPathes || [];

    this.children = (data.children || []).map(
      child => new Node(child, this, requiredAuthorizations),
    );

    this.checked = this.isSelected();
    this.indeterminate = !this.checked && this.haveSelectedChild();
  }

  isAuthorized(): boolean {
    return this.requiredAuthorizations.some(auth =>
      this.id === auth
      || auth.startsWith(this.id + ".")
      || this.matchingPathes.some(
        path => path.sql === auth || auth.startsWith(path.sql + "."),
      ),
    );
  }

  isSelected(): boolean {
    if ((this.requiredAuthorizations || []).includes(this.id)) {
      return true;
    }

    let currentParent = this.parent;
    while (currentParent && currentParent instanceof Node) {
      if ((this.requiredAuthorizations || []).includes(currentParent.id)) {
        return true;
      }
      currentParent = currentParent.parent;
    }

    return false;
  }

  haveSelectedChild(): boolean {
    if (this.isSelected()) {
      return false;
    }

    return this.children.some(
      child => child.isSelected() || child.haveSelectedChild(),
    );
  }

  getState(): { checked: boolean; indeterminate: boolean } {
    if (this.isSelected()) {
      return { checked: true, indeterminate: false };
    }

    if (this.children.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const childrenStates = this.children.map(child => child.getState());
    const allChecked = childrenStates.every(state => state.checked);
    const someChecked = childrenStates.some(state => state.checked || state.indeterminate);

    if (allChecked) {
      return { checked: true, indeterminate: false };
    }
    else if (someChecked) {
      return { checked: false, indeterminate: true };
    }
    else {
      return { checked: false, indeterminate: false };
    }
  }
}
